import { useEffect, useState } from "react";
import {
  Banner,
  Button,
  Card,
  CardGrid,
  Cell,
  FormItem,
  Group,
  Header,
  Input,
  Select,
  Tabs,
  TabsItem,
  Textarea
} from "@vkontakte/vkui";
import { Icon24Add, Icon24RefreshOutline } from "@vkontakte/icons";
import { PERMISSIONS, ROLES, type PermissionCode, type RoleCode } from "@kass/shared";
import { api, errorMessage } from "../api/client";
import { formatDate, formatMoney } from "../shared/format";
import { Page } from "../components/Page";
import { AccessDenied, EmptyState, ErrorState, LoadingState } from "../components/States";

type AdminMember = {
  id: string;
  cardNumber: string;
  level: string;
  expiresAt: string;
  status: string;
  totalSaved: number;
  isVisibleInLeaderboard: boolean;
  user: {
    id: string;
    vkUserId: string;
    firstName: string;
    lastName: string;
    middleName?: string | null;
    fullName: string;
    role: string;
    status: string;
  };
};

type AdminPartner = {
  id: string;
  title: string;
  description: string;
  category: string;
  ownerUserId?: string | null;
  owner?: { id: string; vkUserId: string; fullName: string } | null;
  staff?: Array<{ role: string; user: { id: string; vkUserId: string; fullName: string } }>;
  status: string;
  isPromoted: boolean;
  viewsCount: number;
};

type AdminUser = {
  id: string;
  vkUserId: string;
  fullName: string;
  role: RoleCode;
  permissions: PermissionCode[];
};

type AuditLog = {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  oldValue?: unknown;
  newValue?: unknown;
  createdAt: string;
  actor?: { fullName: string } | null;
};

const levelOptions = [
  { label: "Basic", value: "Basic" },
  { label: "Silver", value: "Silver" },
  { label: "Gold", value: "Gold" },
  { label: "VIP", value: "VIP" }
];

const memberStatusOptions = [
  { label: "active", value: "active" },
  { label: "blocked", value: "blocked" },
  { label: "expired", value: "expired" }
];

const partnerStatusOptions = [
  { label: "active", value: "active" },
  { label: "inactive", value: "inactive" },
  { label: "archived", value: "archived" }
];

function splitUserReferences(value: string) {
  return value
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function partnerOwnerReferences(partner: AdminPartner) {
  const refs = [
    partner.owner ? partner.owner.vkUserId : null,
    ...(partner.staff ?? []).filter((entry) => entry.role === "owner").map((entry) => entry.user.vkUserId)
  ].filter((value): value is string => Boolean(value));

  return [...new Set(refs)];
}

function partnerOwnerNames(partner: AdminPartner) {
  const names = [
    partner.owner ? partner.owner.fullName : null,
    ...(partner.staff ?? []).filter((entry) => entry.role === "owner").map((entry) => entry.user.fullName)
  ].filter((value): value is string => Boolean(value));

  return [...new Set(names)];
}

export function AdminHomePage({ navigate }: { navigate: (path: string) => void }) {
  const [stats, setStats] = useState<Record<string, number> | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get<{ stats: Record<string, number> }>("/admin/stats")
      .then((response) => setStats(response.data.stats))
      .catch((err) => setError(errorMessage(err)));
  }, []);

  return (
    <Page title="Админ-панель">
      {error ? <Banner header="Статистика недоступна" subheader={error} /> : null}
      <Group>
        <Tabs>
          <TabsItem selected onClick={() => navigate("/admin")}>Статистика</TabsItem>
          <TabsItem onClick={() => navigate("/admin/members")}>Члены</TabsItem>
          <TabsItem onClick={() => navigate("/admin/partners")}>Партнёры</TabsItem>
          <TabsItem onClick={() => navigate("/admin/roles")}>Роли</TabsItem>
          <TabsItem onClick={() => navigate("/admin/audit")}>Аудит</TabsItem>
        </Tabs>
      </Group>
      {stats ? (
        <Group header={<Header>Статистика</Header>}>
          <div className="metric-grid">
            <Metric title="Пользователи" value={String(stats.usersCount)} />
            <Metric title="Члены клуба" value={String(stats.membersCount)} />
            <Metric title="Партнёры" value={String(stats.partnersCount)} />
            <Metric title="Фиксации" value={String(stats.redemptionsCount)} />
            <Metric title="Экономия" value={formatMoney(stats.totalDiscounts)} />
            <Metric title="Платежи" value={String(stats.paymentsCount)} />
          </div>
        </Group>
      ) : null}
      <Group header={<Header>Разделы</Header>}>
        <div className="toolbar">
          <Button onClick={() => navigate("/admin/members")}>Члены клуба</Button>
          <Button mode="secondary" onClick={() => navigate("/admin/partners")}>Партнёры</Button>
          <Button mode="secondary" onClick={() => navigate("/admin/roles")}>Роли</Button>
          <Button mode="tertiary" onClick={() => navigate("/admin/audit")}>Журнал действий</Button>
        </div>
      </Group>
      <Group header={<Header>Платежи</Header>}>
        <Banner header="Заглушка платежей" subheader="Модуль платежей заведён в базе и статистике, интеграция провайдера оставлена на следующий этап." />
      </Group>
    </Page>
  );
}

function Metric({ title, value }: { title: string; value: string }) {
  return (
    <div className="metric">
      <div className="muted">{title}</div>
      <div className="metric-value">{value}</div>
    </div>
  );
}

export function AdminMembersPage() {
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    vkUserId: "",
    firstName: "",
    lastName: "",
    middleName: "",
    cardNumber: "",
    level: "Basic",
    expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  });

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ members: AdminMember[] }>("/admin/members", { params: { q: query } });
      setMembers(response.data.members);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async () => {
    try {
      await api.post("/admin/members", {
        ...form,
        expiresAt: new Date(form.expiresAt).toISOString(),
        startsAt: new Date().toISOString()
      });
      setForm({ ...form, vkUserId: "", firstName: "", lastName: "", middleName: "", cardNumber: "" });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const patch = async (member: AdminMember, body: Record<string, unknown>) => {
    await api.patch(`/admin/members/${member.id}`, body);
    await load();
  };

  const remove = async (member: AdminMember) => {
    if (!window.confirm(`Удалить члена ${member.user.fullName} и все связанные данные из базы?`)) {
      return;
    }

    try {
      await api.delete(`/admin/members/${member.id}`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (loading) {
    return (
      <Page title="Члены клуба">
        <LoadingState />
      </Page>
    );
  }

  if (error && members.length === 0) {
    return (
      <Page title="Члены клуба">
        <ErrorState message={error} />
      </Page>
    );
  }

  return (
    <Page title="Члены клуба">
      {error ? <Banner header="Ошибка" subheader={error} /> : null}
      <Group header={<Header>Поиск</Header>}>
        <FormItem top="ФИО, VK ID или карта">
          <Input value={query} onChange={(event) => setQuery(event.target.value)} />
        </FormItem>
        <Button before={<Icon24RefreshOutline />} onClick={load}>Найти</Button>
      </Group>

      <Group header={<Header>Добавить члена</Header>}>
        <div className="form-row">
          <FormItem top="VK-ссылка, username или VK ID">
            <Input
              placeholder="https://vk.com/id1002, @username или 1002"
              value={form.vkUserId}
              onChange={(event) => setForm({ ...form, vkUserId: event.target.value })}
            />
          </FormItem>
          <FormItem top="Номер карты"><Input value={form.cardNumber} onChange={(event) => setForm({ ...form, cardNumber: event.target.value })} /></FormItem>
        </div>
        <div className="form-row">
          <FormItem top="Фамилия"><Input value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} /></FormItem>
          <FormItem top="Имя"><Input value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} /></FormItem>
        </div>
        <div className="form-row">
          <FormItem top="Отчество"><Input value={form.middleName} onChange={(event) => setForm({ ...form, middleName: event.target.value })} /></FormItem>
          <FormItem top="Уровень">
            <Select
              value={form.level}
              onChange={(event) => setForm({ ...form, level: event.target.value })}
              options={levelOptions}
            />
          </FormItem>
        </div>
        <FormItem top="Дата окончания">
          <Input type="date" value={form.expiresAt} onChange={(event) => setForm({ ...form, expiresAt: event.target.value })} />
        </FormItem>
        <Button before={<Icon24Add />} onClick={create}>Добавить</Button>
      </Group>

      <Group header={<Header>Список</Header>}>
        {members.map((member) => (
          <Card key={member.id} mode="shadow" style={{ marginBottom: 10 }}>
            <Cell subtitle={`VK ${member.user.vkUserId} · карта ${member.cardNumber}`} after={formatMoney(member.totalSaved)}>
              {member.user.fullName}
            </Cell>
            <div className="toolbar" style={{ padding: "0 12px 12px" }}>
              <Select value={member.level} onChange={(event) => patch(member, { level: event.target.value })} options={levelOptions} />
              <Select value={member.status} onChange={(event) => patch(member, { status: event.target.value })} options={memberStatusOptions} />
              <Button size="s" mode="secondary" onClick={() => patch(member, { expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() })}>
                Продлить на год
              </Button>
              <Button size="s" mode="secondary" appearance="negative" onClick={() => remove(member)}>
                Удалить из базы
              </Button>
            </div>
          </Card>
        ))}
      </Group>
    </Page>
  );
}

export function AdminPartnersPage() {
  const [partners, setPartners] = useState<AdminPartner[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    ownerRefs: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ partners: AdminPartner[] }>("/admin/partners");
      setPartners(response.data.partners);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async () => {
    try {
      await api.post("/admin/partners", {
        title: form.title,
        description: form.description,
        category: form.category,
        status: "active",
        ownerUserIds: splitUserReferences(form.ownerRefs)
      });
      setForm({ title: "", description: "", category: "", ownerRefs: "" });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const patch = async (partner: AdminPartner, body: Record<string, unknown>) => {
    await api.patch(`/admin/partners/${partner.id}`, body);
    await load();
  };

  const remove = async (partner: AdminPartner) => {
    if (!window.confirm(`Удалить партнёра «${partner.title}» и все связанные данные из базы?`)) {
      return;
    }

    try {
      await api.delete(`/admin/partners/${partner.id}`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (loading) {
    return (
      <Page title="Партнёры">
        <LoadingState />
      </Page>
    );
  }

  if (error && partners.length === 0) {
    return (
      <Page title="Партнёры">
        <AccessDenied />
      </Page>
    );
  }

  return (
    <Page title="Партнёры">
      {error ? <Banner header="Ошибка" subheader={error} /> : null}
      <Group header={<Header>Добавить партнёра</Header>}>
        <FormItem top="Название"><Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></FormItem>
        <FormItem top="Описание"><Textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></FormItem>
        <div className="form-row">
          <FormItem top="Категория"><Input value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} /></FormItem>
          <FormItem top="Владельцы: VK-ссылки, короткие адреса, VK ID или userId">
            <Textarea
              placeholder={"Например:\nhttps://vk.com/id1002\n@club.owner\nhttps://vk.com/ivan.petrov"}
              value={form.ownerRefs}
              onChange={(event) => setForm({ ...form, ownerRefs: event.target.value })}
            />
          </FormItem>
        </div>
        <Button before={<Icon24Add />} onClick={create}>Добавить</Button>
      </Group>
      <Group header={<Header>Список</Header>}>
        {partners.map((partner) => (
          <Card key={partner.id} mode="shadow" style={{ marginBottom: 10 }}>
            <Cell
              subtitle={`${partner.category} · просмотры ${partner.viewsCount} · владельцы: ${partnerOwnerNames(partner).join(", ") || "не назначены"}`}
              after={partner.status}
            >
              {partner.title}
            </Cell>
            <div className="toolbar" style={{ padding: "0 12px 12px" }}>
              <Select value={partner.status} onChange={(event) => patch(partner, { status: event.target.value })} options={partnerStatusOptions} />
              <Textarea
                key={partnerOwnerReferences(partner).join(",")}
                placeholder="VK-ссылки, короткие адреса, VK ID или userId владельцев"
                defaultValue={partnerOwnerReferences(partner).join("\n")}
                onBlur={(event) => patch(partner, { ownerUserIds: splitUserReferences(event.target.value) })}
              />
              <Button size="s" mode="secondary" onClick={() => patch(partner, { isPromoted: !partner.isPromoted })}>
                {partner.isPromoted ? "Снять продвижение" : "Продвигать"}
              </Button>
              <Button size="s" mode="secondary" appearance="negative" onClick={() => remove(partner)}>
                Удалить из базы
              </Button>
            </div>
          </Card>
        ))}
      </Group>
    </Page>
  );
}

export function AdminRolesPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ users: AdminUser[] }>("/admin/users");
      setUsers(response.data.users);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const changeRole = async (user: AdminUser, role: RoleCode) => {
    try {
      await api.patch(`/admin/users/${user.id}/roles`, { role });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const changePermissions = async (user: AdminUser, raw: string) => {
    const permissions = raw.split(",").map((item) => item.trim()).filter(Boolean);
    try {
      await api.patch(`/admin/users/${user.id}/permissions`, { permissions });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const remove = async (user: AdminUser) => {
    if (!window.confirm(`Удалить пользователя ${user.fullName} и все связанные данные из базы?`)) {
      return;
    }

    try {
      await api.delete(`/admin/users/${user.id}`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (loading) {
    return (
      <Page title="Роли">
        <LoadingState />
      </Page>
    );
  }

  if (error && users.length === 0) {
    return (
      <Page title="Роли">
        <AccessDenied />
      </Page>
    );
  }

  return (
    <Page title="Роли и права">
      {error ? <Banner header="Ошибка" subheader={error} /> : null}
      <Group header={<Header>Пользователи</Header>}>
        {users.map((user) => (
          <Card key={user.id} mode="shadow" style={{ marginBottom: 10 }}>
            <Cell subtitle={`VK ${user.vkUserId}`}>{user.fullName}</Cell>
            <div className="toolbar" style={{ padding: "0 12px 12px" }}>
              <Select
                value={user.role}
                onChange={(event) => changeRole(user, event.target.value as RoleCode)}
                options={ROLES.map((role) => ({ label: role, value: role }))}
              />
              <Input
                placeholder="permissions через запятую"
                defaultValue={user.permissions.join(", ")}
                onBlur={(event) => changePermissions(user, event.target.value)}
              />
              <Button size="s" mode="secondary" appearance="negative" onClick={() => remove(user)}>
                Удалить пользователя
              </Button>
            </div>
          </Card>
        ))}
      </Group>
      <Group header={<Header>Доступные permissions</Header>}>
        <Textarea value={PERMISSIONS.join(", ")} readOnly />
      </Group>
    </Page>
  );
}

export function AdminAuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get<{ logs: AuditLog[] }>("/admin/audit")
      .then((response) => {
        setLogs(response.data.logs);
        setError("");
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Page title="Аудит">
        <LoadingState />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Аудит">
        <AccessDenied />
      </Page>
    );
  }

  return (
    <Page title="Журнал действий">
      <Group header={<Header>Последние события</Header>}>
        {logs.length ? (
          logs.map((log) => (
            <Cell key={log.id} subtitle={`${log.actor?.fullName || "Система"} · ${formatDate(log.createdAt)} · ${log.entityType}:${log.entityId}`}>
              {log.action}
            </Cell>
          ))
        ) : (
          <EmptyState text="Журнал пуст." />
        )}
      </Group>
      <Group header={<Header>Детали последней записи</Header>}>
        {logs[0] ? <Textarea readOnly value={JSON.stringify({ oldValue: logs[0].oldValue, newValue: logs[0].newValue }, null, 2)} /> : null}
      </Group>
    </Page>
  );
}
